<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useRoute } from "vue-router";

const page = usePage();
const nuxtApp = useNuxtApp();
const route = useRoute(); // Get the current route

const normalizePath = (path) => path?.replace(/^\/|\/$/g, "").toLowerCase();

const essentialInfo = computed(() => {
  const current = page.value;
  if (!current?.uri || !route.path) {
    return current?.essentialInfo ?? [];
  }

  return (
    current?.essentialInfo?.filter((item) => {
      return normalizePath(item?.uri) !== normalizePath(route.path);
    }) ?? []
  );
});

const trekRefs = ref<HTMLElement[]>([]);
function addRef(el, index) {
  if (el) {
    trekRefs.value[index] = el;
  }
}
async function setupAnimations() {
  if (!process.client) return;

  try {
    const { gsap } = await import("gsap");
    const { ScrollTrigger } = await import("gsap/ScrollTrigger");

    trekRefs.value.forEach((el) => {
      if (!el) return;

      // Check if the element is below the fold
      const rect = el.getBoundingClientRect();
      const isAboveFold = rect.top < window.innerHeight;

      if (isAboveFold) {
        // Ensure items above the fold are fully visible on page load
        gsap.set(el, { opacity: 1, scale: 1 });
      } else {
        // Animate items below the fold
        gsap.fromTo(
          el,
          { opacity: 0, scale: 0.75 },
          {
            opacity: 1,
            scale: 1,
            duration: 1.5,
            scrollTrigger: {
              trigger: el,
              start: "top 90%",
              end: "bottom 80%",
              toggleActions: "play none none reverse",
              scrub: true,
              markers: false,
            },
            ease: "power3.out",
          }
        );
      }
    });
  } catch (error) {
    console.error("Failed to setup animations in InfoNav:", error);
  }
}

async function destroyAnimations() {
  if (!process.client) return;

  try {
    const { ScrollTrigger } = await import("gsap/ScrollTrigger");
    // Kill all ScrollTriggers to clean up
    ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
  } catch (error) {
    console.error("Failed to destroy animations in InfoNav:", error);
  }
}

onMounted(async () => {
  if (!process.client) return;

  try {
    // Dynamically import GSAP and ScrollTrigger
    const { gsap } = await import("gsap");
    const { ScrollTrigger } = await import("gsap/ScrollTrigger");

    // Create GSAP matchMedia instance for responsive animations
    const mm = gsap.matchMedia();

    // Add a responsive context
    mm.add("(min-width: 0px)", () => {
      // Setup animations for current screen size
      setupAnimations();

      // Return cleanup function
      return () => {
        destroyAnimations();
      };
    });

    // Refresh ScrollTrigger on resize
    const refreshOnResize = () => ScrollTrigger.refresh();
    window.addEventListener("resize", refreshOnResize);

    // Cleanup listener
    onUnmounted(() => {
      mm.revert(); // Revert matchMedia contexts
      window.removeEventListener("resize", refreshOnResize);
    });
  } catch (error) {
    console.error("Failed to load GSAP in InfoNav:", error);
  }
});

// On Suspense resolved event
nuxtApp.hook("page:finish", async () => {
  setTimeout(async () => {
    await setupAnimations();
    try {
      const { ScrollTrigger } = await import("gsap/ScrollTrigger");
      ScrollTrigger.refresh();
    } catch (error) {
      console.error("Failed to refresh ScrollTrigger in InfoNav:", error);
    }
  }, 750);
});
</script>

<template>
  <section class="treks-nav-wrap">
    <div
      v-for="(item, index) in essentialInfo"
      :key="index"
      :ref="(el) => addRef(el, index)"
      class="trek"
    >
      <NuxtLink class="btn from-left" :to="`/${item?.uri}`">
        <div class="trek-image">
          <NuxtPicture
            v-if="item?.coverImage"
            loading="lazy"
            :src="item?.coverImage.url"
            :width="item?.coverImage.width"
            :height="item?.coverImage.height"
            :alt="item?.coverImage.alt"
            sizes="sm:100vw md:50vw lg:600px"
          />
        </div>
        <div class="trek-title">
          <h3>{{ item?.title }}</h3>
        </div>
      </NuxtLink>
    </div>
    <div class="trek extra-box">
      <p>
        If you have any questions that haven't been covered on this page please contact
        us.
      </p>
      <NuxtLink class="btn from-left" to="/get-in-touch">Contact Us</NuxtLink>
    </div>
  </section>
</template>
